/** Enum values mirrored from the backend. The drift guard in
 *  `tests/contracts/EnumDrift.test.ts` parses the backend source and fails if
 *  these lists stop matching, so a value the API would reject cannot reach a form. */

/** backend/src/modules/m17-master-config/enums/m17.enums.ts :: M17RateType */
export const M17_RATE_TYPES = [
  'PER_SQ_FT', 'PER_SQ_METER', 'PER_BOARD', 'PER_POLE', 'PER_FLEX',
  'PER_HOARDING', 'PER_WALL_PAINTING', 'PER_UNIT', 'PER_ACTIVITY',
  'FIXED_COST', 'COMPOSITE_COST', 'CAMPAIGN_COST',
] as const;

/** backend/src/modules/m17-master-config/enums/m17.enums.ts :: M17RateUnit */
export const M17_RATE_UNITS = [
  'SQ_FT', 'SQ_METER', 'UNIT', 'BOARD', 'ACTIVITY', 'LUMP_SUM',
] as const;

/** backend/src/modules/m17-master-config/services/gst-config.service.ts :: PlaceOfSupplyRule */
export const GST_PLACE_OF_SUPPLY_RULES = [
  'SITE_STATE', 'VENDOR_STATE', 'RECIPIENT_STATE',
] as const;

/** backend/src/modules/m07-branding-officer/enums/m07.enums.ts :: AssignmentStatus */
export const M07_ASSIGNMENT_STATUSES = [
  'PENDING_ACCEPTANCE', 'ACCEPTED', 'REJECTED', 'CLARIFICATION',
  'EXPIRED', 'REASSIGNED', 'CANCELLED',
] as const;

/** backend/src/modules/m08-vendor-operations/enums/m08.enums.ts :: PainterAssignmentStatus */
// SENT_BACK, not REJECTED: a hand-back says the job reached the wrong person
// and must be picked up again, where a rejection would judge the work and end
// the line. The backend dropped REJECTED when send-back was built. A submitted
// job is COMPLETED on the painter's side (owner, 25 Oct); there is no
// "awaiting submission".
export const M08_PAINTER_ASSIGNMENT_STATUSES = [
  'PENDING', 'ACCEPTED', 'EXECUTION_IN_PROGRESS',
  'REASSIGNED', 'SENT_BACK', 'CANCELLED', 'COMPLETED',
] as const;

/** backend/src/modules/m08-vendor-operations/enums/m08.enums.ts :: PainterPriority */
export const M08_PAINTER_PRIORITIES = [
  'LOW', 'MEDIUM', 'HIGH', 'CRITICAL',
] as const;

/** backend/src/modules/m08-vendor-operations/enums/m08.enums.ts :: PainterReassignReason */
export const M08_PAINTER_REASSIGN_REASONS = [
  'PAINTER_UNAVAILABLE', 'UNAVAILABLE', 'EMERGENCY', 'PERFORMANCE_ISSUE',
  'WORKLOAD_BALANCING', 'CUSTOMER_REQUEST',
] as const;

/** backend/src/modules/m13-vendor-billing/enums/m13.enums.ts :: M13ClaimStatus,
 *  in the order a bill moves through them. */
export const M13_CLAIM_STATUSES = [
  'DRAFT', 'SUBMITTED', 'PENDING_VERIFICATION', 'APPROVED_FOR_FINANCE', 'PAID',
  'RETURNED', 'REJECTED', 'WITHDRAWN',
] as const;

/** backend/src/modules/m15-payment-processing/enums/m15.enums.ts :: M15PaymentMode */
export const M15_PAYMENT_MODES = [
  'NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'DD', 'INTERNAL_TRANSFER',
] as const;

/** backend/src/modules/m15-payment-processing/enums/m15.enums.ts :: M15HoldReasonCode -
 *  the reasons a payment may be held for (PaymentHoldService refuses others). */
export const M15_HOLD_REASONS = [
  'COMPLIANCE', 'DISPUTE', 'INVESTIGATION', 'LEGAL', 'VENDOR_REQUEST',
  'SYSTEM', 'AUDIT', 'BUDGET', 'MANAGEMENT',
] as const;

export type M17RateType = (typeof M17_RATE_TYPES)[number];
export type M17RateUnit = (typeof M17_RATE_UNITS)[number];
export type GstPlaceOfSupplyRule = (typeof GST_PLACE_OF_SUPPLY_RULES)[number];
export type M07AssignmentStatus = (typeof M07_ASSIGNMENT_STATUSES)[number];
export type M08PainterAssignmentStatus = (typeof M08_PAINTER_ASSIGNMENT_STATUSES)[number];
export type M08PainterPriority = (typeof M08_PAINTER_PRIORITIES)[number];
export type M08PainterReassignReason = (typeof M08_PAINTER_REASSIGN_REASONS)[number];

/** Named so a screen never retypes the literal. Vendor acceptance opens at
 *  PENDING_ACCEPTANCE; painter allocation is refused by
 *  PainterAssignmentService.assign until the row reaches ACCEPTED. */
export const ASSIGNMENT_PENDING_ACCEPTANCE = 'PENDING_ACCEPTANCE';
export const ASSIGNMENT_ACCEPTED = 'ACCEPTED';
export const ASSIGNMENT_CLARIFICATION = 'CLARIFICATION';
export const DEFAULT_PAINTER_PRIORITY: M08PainterPriority = 'MEDIUM';
export const DEFAULT_PAINTER_REASSIGN_REASON: M08PainterReassignReason = 'PAINTER_UNAVAILABLE';

/** Where the drift guard looks for each mirrored enum. */
export const MIRRORED_ENUMS = [
  {
    name: 'M17RateType',
    backendFile: 'backend/src/modules/m17-master-config/enums/m17.enums.ts',
    values: M17_RATE_TYPES as readonly string[],
  },
  {
    name: 'M17RateUnit',
    backendFile: 'backend/src/modules/m17-master-config/enums/m17.enums.ts',
    values: M17_RATE_UNITS as readonly string[],
  },
  {
    name: 'PlaceOfSupplyRule',
    backendFile: 'backend/src/modules/m17-master-config/services/gst-config.service.ts',
    values: GST_PLACE_OF_SUPPLY_RULES as readonly string[],
  },
  {
    name: 'AssignmentStatus',
    backendFile: 'backend/src/modules/m07-branding-officer/enums/m07.enums.ts',
    values: M07_ASSIGNMENT_STATUSES as readonly string[],
  },
  {
    name: 'PainterAssignmentStatus',
    backendFile: 'backend/src/modules/m08-vendor-operations/enums/m08.enums.ts',
    values: M08_PAINTER_ASSIGNMENT_STATUSES as readonly string[],
  },
  {
    name: 'PainterPriority',
    backendFile: 'backend/src/modules/m08-vendor-operations/enums/m08.enums.ts',
    values: M08_PAINTER_PRIORITIES as readonly string[],
  },
  {
    name: 'PainterReassignReason',
    backendFile: 'backend/src/modules/m08-vendor-operations/enums/m08.enums.ts',
    values: M08_PAINTER_REASSIGN_REASONS as readonly string[],
  },
  {
    name: 'M13ClaimStatus',
    backendFile: 'backend/src/modules/m13-vendor-billing/enums/m13.enums.ts',
    values: M13_CLAIM_STATUSES as readonly string[],
  },
  {
    name: 'M15PaymentMode',
    backendFile: 'backend/src/modules/m15-payment-processing/enums/m15.enums.ts',
    values: M15_PAYMENT_MODES as readonly string[],
  },
  {
    name: 'M15HoldReasonCode',
    backendFile: 'backend/src/modules/m15-payment-processing/enums/m15.enums.ts',
    values: M15_HOLD_REASONS as readonly string[],
  },
] as const;

/**
 * GSTIN format, mirrored from backend/src/common/gstin.ts.
 *
 * The pattern had been written out ten times across the two codebases and the
 * copies had drifted — four allowed '0' as the entity number at position 13 and
 * five did not — so the same GSTIN was accepted by the company screen and
 * rejected by the vendor screen. The drift guard compares this against the
 * backend source, so a form can no longer accept what the API will refuse.
 */
export const GSTIN_PATTERN_SOURCE = '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$';

export const GSTIN_PATTERN = new RegExp(GSTIN_PATTERN_SOURCE);

export const GSTIN_BACKEND_FILE = 'backend/src/common/gstin.ts';

export function isGstin(value: string | null | undefined): boolean {
  return !!value && GSTIN_PATTERN.test(value);
}

/**
 * PAN format, mirrored from backend/src/common/pan.ts. The company, vendor and
 * customer screens each wrote it out; the drift guard compares this against
 * the backend source.
 */
export const PAN_PATTERN_SOURCE = '^[A-Z]{5}[0-9]{4}[A-Z]$';

export const PAN_PATTERN = new RegExp(PAN_PATTERN_SOURCE);

export const PAN_BACKEND_FILE = 'backend/src/common/pan.ts';
